import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {type ExperimentStore, resolveExperimentStore} from '@github-ui/experiments/internal'
import {ExperimentDocumentProvider} from '@github-ui/experiments/internal/ExperimentProvider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getQueryClient} from '@github-ui/query-client'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {ToastContextProvider} from '@github-ui/toast/ToastContext'
import {ThemeProvider} from '@primer/react/next'
import {QueryClientProvider} from '@tanstack/react-query'
import type {ReactNode} from 'react'

import {IsDataRouterEnabledContextProvider} from './future/IsDataRouterEnabled'
import {ReportErrorContextProvider} from './future/ReportErrorContext'
import {PrimerFeatureFlags} from './PrimerFeatureFlags'
import useColorModes from './use-color-modes'

interface Props {
  appName: string
  children?: ReactNode
  dataRouterEnabled: boolean
  ssrExperimentStore?: ExperimentStore
}

const metadata = {}

/**
 * This component provides the _base_ context for both apps and partials.
 * It should provide everything needed to render with styles, themes, and i18n.
 */
export function BaseProviders({appName, children, dataRouterEnabled, ssrExperimentStore}: Props) {
  const {colorMode, dayScheme, nightScheme} = useColorModes()

  const queryClient = getQueryClient()
  const providers = (
    <QueryClientProvider client={queryClient}>
      <AnalyticsProvider appName={appName} category="" metadata={metadata}>
        <PrimerFeatureFlags>
          <ThemeProvider colorMode={colorMode} dayScheme={dayScheme} nightScheme={nightScheme} contextOnly>
            <IsDataRouterEnabledContextProvider enabled={dataRouterEnabled}>
              <ReportErrorContextProvider appName={appName}>
                <ToastContextProvider>{children}</ToastContextProvider>
              </ReportErrorContextProvider>
            </IsDataRouterEnabledContextProvider>
          </ThemeProvider>
        </PrimerFeatureFlags>
      </AnalyticsProvider>
    </QueryClientProvider>
  )

  if (!isFeatureEnabled('client_experiment_assignment_forwarding')) {
    return providers
  }

  const resolvedExperimentStore = resolveExperimentStore({ssrExperimentStore, document: ssrSafeDocument})
  return <ExperimentDocumentProvider store={resolvedExperimentStore}>{providers}</ExperimentDocumentProvider>
}
